import {
  type WorkspaceSearchQuery,
  workspaceSearchQuery,
  WorkspaceSearchSourceKind,
} from '@affine/graphql';
import type { I18nString } from '@affine/i18n';
import { EmailIcon } from '@blocksuite/icons/rc';
import { type Effect, effect, Entity, LiveData } from '@toeverything/infra';
import { truncate } from 'lodash-es';
import { catchError, debounceTime, from, map, of, switchMap, tap } from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchGroup } from '../types/group';
import type { QuickSearchItem } from '../types/item';

export type MailSearchResult =
  WorkspaceSearchQuery['workspace']['workspaceSearch'][number];

export interface MailPayload {
  route: string;
  messageId?: string | null;
  accountId?: string | null;
  folderId?: string | null;
  subject?: string | null;
  from?: string | null;
  receivedAt?: string | null;
}

const MAIL_SEARCH_LIMIT = 20;
const MAIL_SEARCH_DEBOUNCE_MS = 350;

export function mapWorkspaceSearchMailResults(
  query: string,
  results: readonly MailSearchResult[]
): QuickSearchItem<'mail', MailPayload>[] {
  const group: QuickSearchGroup = {
    id: 'mail',
    label: {
      i18nKey: 'com.streamlain.quicksearch.group.mail',
      options: { query: truncate(query) },
    },
    score: 6,
  };

  return results
    .filter(result => result.kind === WorkspaceSearchSourceKind.mail_message)
    .map(result => ({
      id: result.id,
      source: 'mail' as const,
      group,
      label: {
        title: result.title,
        subTitle: result.snippet || result.subtitle || undefined,
      },
      score: result.score,
      icon: EmailIcon,
      timestamp: parseSearchTimestamp(result.receivedAt),
      badge: {
        i18nKey: result.attachmentHits.length
          ? 'com.streamlain.quicksearch.sourceType.mailWithAttachments'
          : 'com.streamlain.quicksearch.sourceType.mail',
        options: { count: result.attachmentHits.length },
      },
      payload: {
        route: result.openTarget.route,
        messageId: result.mailMessageId,
        accountId: result.mailAccountId,
        folderId: result.mailFolderId,
        subject: result.mailSubject,
        from: result.mailFrom,
        receivedAt: result.receivedAt,
      },
    }));
}

export class MailQuickSearchSession
  extends Entity
  implements QuickSearchSession<'mail', MailPayload>
{
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  items$ = new LiveData<QuickSearchItem<'mail', MailPayload>[]>([]);
  error$ = new LiveData<any>(null);
  isLoading$ = new LiveData(false);
  status$ = new LiveData<I18nString | null>(null);

  private readonly mailUnavailableStatus: I18nString = {
    i18nKey: 'com.streamlain.quicksearch.status.mail-unavailable',
  };

  private readonly queryEffect = effect(
    tap((query: string) => {
      const keyword = query.trim();
      const server = this.workspaceServerService.server;
      this.error$.next(null);
      this.items$.next([]);
      this.isLoading$.next(Boolean(keyword && server));
      this.status$.next(keyword && !server ? this.mailUnavailableStatus : null);
    }),
    debounceTime(MAIL_SEARCH_DEBOUNCE_MS),
    switchMap((query: string) => {
      const keyword = query.trim();
      const server = this.workspaceServerService.server;
      if (!keyword || !server) {
        return of([]);
      }

      return from(
        server.gql({
          query: workspaceSearchQuery,
          variables: {
            id: this.workspaceService.workspace.id,
            input: {
              keyword,
              limit: MAIL_SEARCH_LIMIT,
              sourceKinds: [WorkspaceSearchSourceKind.mail_message],
            },
          },
        }) as Promise<WorkspaceSearchQuery>
      ).pipe(
        map(result =>
          mapWorkspaceSearchMailResults(
            keyword,
            result.workspace.workspaceSearch
          )
        ),
        catchError(error => {
          console.warn(
            '[MailQuickSearchSession] Mail search unavailable',
            error
          );
          this.error$.next(null);
          this.status$.next(this.mailUnavailableStatus);
          return of([]);
        })
      );
    }),
    tap(items => {
      this.items$.next(items);
      this.isLoading$.next(false);
    })
  ) as Effect<string>;

  query(query: string) {
    this.queryEffect(query);
  }

  override dispose(): void {
    this.queryEffect.unsubscribe();
  }
}

function parseSearchTimestamp(value: string | null | undefined) {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}
